import { useCallback, useLayoutEffect, useState } from 'react'
import { TargetType, TimeLine } from '../../../../generated/graphql'
import { useGetHistoryLazyQuery } from '../../../../generated/graphql'

export const ITEMS_PER_PAGE = 15 // Adjust this number as needed
export type TimeLineItem = Partial<TimeLine> & {
  chunk: number
  loading: boolean
}

export const useTimeLine = ({
  targetId,
  targetType,
}: {
  targetId: string
  targetType: TargetType
}) => {
  const [loadingChunk, setLoadingChunk] = useState<boolean>(false)
  const [totalItems, setTotalItems] = useState<number>(0)
  const [chunks, setChunks] = useState<Array<TimeLineItem[]>>([])

  const [getHistory, { loading }] = useGetHistoryLazyQuery()

  const getChunk = (page: number) => ({
    variables: {
      input: {
        targetId,
        targetType,
        limit: ITEMS_PER_PAGE,
        offset: page * ITEMS_PER_PAGE,
      },
    },
  })

  // Load specific chunk and stores it in the chunks array
  const loadChunk = useCallback(
    (page: number): Promise<Array<TimeLineItem>> => {
      if (!targetType || !targetId) {
        return Promise.resolve([])
      }
      setLoadingChunk(true)

      return new Promise((resolve) => {
        getHistory(getChunk(page))
          .then(({ data }) => {
            const newChunks = data?.getHistory.results.map((item) => ({
              ...item,
              chunk: page,
              loading: false,
            })) as Array<TimeLineItem>

            setChunks((chunks) => {
              chunks[page] = newChunks
              return [...chunks]
            })
            setLoadingChunk(false)
            resolve(newChunks)
          })
          .catch(() => {
            setLoadingChunk(false)
          })
          .finally(() => {
            resolve([])
          })
      })
    },
    [getHistory, setLoadingChunk]
  )

  // On init we load the last and the first chunk allowing scroll and playback
  useLayoutEffect(() => {
    // 0 Is the latest chunk
    getHistory(getChunk(0)).then(({ data }) => {
      // Calculate total of pages to build the chunk
      const totalItems = data?.getHistory.total || 0
      const totalChunks = Math.floor(totalItems / ITEMS_PER_PAGE)
      setTotalItems(totalItems)

      const chunks = Array.from(
        { length: totalChunks + 1 },
        (_, index) => [{ loading: true, chunk: index }] as [TimeLineItem]
      )
      // @ts-ignore: Direct assignment is valid here
      chunks[0] = data?.getHistory.results.map((item) => ({
        ...item,
        chunk: 0,
        loading: false,
      })) as Array<TimeLineItem>

      setChunks(chunks)

      if (totalChunks <= 0) {
        return
      }
      // Get the last chunk in case we click replay we have already the data
      loadChunk(totalChunks)
    })
  }, [getHistory, targetId, targetType])
  // As the user can jump between chunks we need to keep track of the chunks that are loaded

  // useSocketRoom(targetId).event(SocketEvents.TimelineUpdate, () => {
  //   refetch()
  // })

  return { chunks, loading, loadChunk, loadingChunk, totalItems }
}
